import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Alert, Modal } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from '../../../hooks/useDebounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTrashAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import { BusinessCategory1, BusinessCategory2, BusinessCategory3, Candidate, PaginationData, SortConfig } from '../../../types';
import { getSortIcon, renderPagination, validateEmail, containsEmoji, validatePhoneNumber, validatePassword } from '../../../utils/Utils';
import { PASSWORD_VALIDATION } from '../../../utils/Constants';
import AdminHeader from '../layout/AdminHeader';
import AdminNavigation from '../layout/AdminNavigation';
import BusinessCategorySelector from './BusinessCategorySelector';
import './AdminClientDetail.css';

interface User {
  id: number;
  name: string;
  email: string;
  contact_person?: string;
  phone?: string;
  business_category1_id?: number;
  business_category2_id?: number;
  business_category3_id?: number;
  internal_memo?: string;
  candidates_count: number;
  created_at: string;
  candidates?: Candidate[];
}

const AdminClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [candidates, setCandidates] = useState<PaginationData>({} as PaginationData);
  const [loading, setLoading] = useState<boolean>(false);
  const [candidatesLoading, setCandidatesLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editedUser, setEditedUser] = useState<User & { password?: string } | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState<boolean>(false);
  const [allCategory1Data, setAllCategory1Data] = useState<BusinessCategory1[]>([]);
  const [allCategory2Data, setAllCategory2Data] = useState<BusinessCategory2[]>([]);
  const [allCategory3Data, setAllCategory3Data] = useState<BusinessCategory3[]>([]);
  const [categoryNames, setCategoryNames] = useState<{
    category1?: string;
    category2?: string;
    category3?: string;
  }>({});
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchUserDetails();
    loadAllCategoryData();
  }, [id, navigate]);

  useEffect(() => {
    if (user) {
      fetchCandidates();
    }
  }, [currentPage, sortConfig, debouncedSearchTerm, user]);

  useEffect(() => {
    if (user && allCategory1Data.length > 0 && allCategory2Data.length > 0 && allCategory3Data.length > 0) {
      if (user.business_category1_id) {
        loadCategoryNames(user.business_category1_id, user.business_category2_id, user.business_category3_id);
      } else {
        setCategoryNames({});
      }
    }
  }, [user, allCategory1Data, allCategory2Data, allCategory3Data]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/admin/users/${id}`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('クライアント詳細の取得に失敗しました。');
      setLoading(false);
    }
  };

  const fetchCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction,
        user_id: id!,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      });

      const response = await axios.get(`/admin/candidates?${params}`);
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setError('候補者一覧の取得に失敗しました。');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const loadAllCategoryData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');

      const [response1, response2, response3] = await Promise.all([
        axios.get('/admin/business-categories/1', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/admin/business-categories/2', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/admin/business-categories/3', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAllCategory1Data(response1.data);
      setAllCategory2Data(response2.data);
      setAllCategory3Data(response3.data);
    } catch (error) {
      console.error('業界カテゴリデータの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryNames = async (category1Id?: number, category2Id?: number, category3Id?: number) => {
    try {
      const names: { category1?: string; category2?: string; category3?: string } = {};

      if (category1Id) {
        const category1 = allCategory1Data.find((cat: BusinessCategory1) => cat.id === category1Id);
        if (category1) names.category1 = category1.name;

        if (category2Id) {
          const category2 = allCategory2Data.find((cat: BusinessCategory2) => cat.business_category1_id === category1Id && cat.id === category2Id);
          if (category2) names.category2 = category2.name;

          if (category3Id) {
            const category3 = allCategory3Data.find((cat: BusinessCategory3) => 
              cat.business_category1_id === category1Id && 
              cat.business_category2_id === category2Id && 
              cat.id === category3Id
            );
            if (category3) names.category3 = category3.name;
          }
        }
      }

      setCategoryNames(names);
    } catch (error) {
      console.error('業界カテゴリ名の取得に失敗しました:', error);
    }
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleDelete = async (candidateId: number, candidateName: string) => {
    if (!window.confirm(`${candidateName}さんの情報を削除してもよろしいですか？`)) {
      return;
    }

    try {
      await axios.delete(`/admin/candidates/${candidateId}`);
      alert('候補者が削除されました。');
      fetchCandidates();
    } catch (error: any) {
      alert(error.response?.data?.message || '削除に失敗しました。');
    }
  };

  const handleEditClient = () => {
    if (user) {
      setEditedUser({
        ...user, 
        password: '',
        business_category1_id: user.business_category1_id || 0,
        business_category2_id: user.business_category2_id || 0,
        business_category3_id: user.business_category3_id || 0
      });
      setShowEditModal(true);
      setErrors({});
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedUser(prev => prev ? {
      ...prev,
      [name]: value
    } : null);
  };

  const validateEditForm = async (): Promise<{[key: string]: string}> => {
    const newErrors: {[key: string]: string} = {};

    if (!editedUser) return newErrors;

    if (!editedUser.name?.trim()) {
      newErrors.name = 'クライアント名を入力してください。';
    } else if (containsEmoji(editedUser.name)) {
      newErrors.name = '絵文字は使用できません。';
    }

    if (!editedUser.contact_person?.trim()) {
      newErrors.contact_person = '担当者名を入力してください。';
    } else if (containsEmoji(editedUser.contact_person)) {
      newErrors.contact_person = '絵文字は使用できません。';
    }

    if (!editedUser.phone?.trim()) {
      newErrors.phone = '電話番号を入力してください。';
    } else if (!validatePhoneNumber(editedUser.phone)) {
      newErrors.phone = '不正な電話番号です。';
    } else if (containsEmoji(editedUser.phone)) {
      newErrors.phone = '絵文字は使用できません。';
    }

    if (!editedUser.email?.trim()) {
      newErrors.email = 'メールアドレスを入力してください。';
    } else if (!validateEmail(editedUser.email)) {
      newErrors.email = '不正なメールアドレスです。';
    } else if (containsEmoji(editedUser.email)) {
      newErrors.email = '絵文字は使用できません。';
    } else {
      try {
        const response = await axios.get(`/admin/users/check-email?email=${encodeURIComponent(editedUser.email)}&exclude=${id}`);
        if (response.data.exists) {
          newErrors.email = '既に登録済みのメールアドレスです。';
        }
      } catch (error) {
        console.error('Email check error:', error);
        newErrors.email = 'メールアドレスの重複チェック中にエラーが発生しました。もう一度お試しください。';
      }
    }

/*
    if (editedUser.password && editedUser.password.trim()) {
      if (!validatePassword(editedUser.password)) {
        newErrors.password = PASSWORD_VALIDATION.ERROR_MESSAGE;
      } else if (containsEmoji(editedUser.password)) {
        newErrors.password = '絵文字は使用できません。';
      }
    }
*/

    if (editedUser.internal_memo && containsEmoji(editedUser.internal_memo)) {
      newErrors.internal_memo = '絵文字は使用できません。';
    }

    return newErrors;
  };

  const handleSaveClient = async () => {
    if (!editedUser) return;

    const newErrors = await validateEditForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsEditSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const updateData: any = {
        name: editedUser.name,
        email: editedUser.email,
        contact_person: editedUser.contact_person,
        phone: editedUser.phone,
        business_category1_id: editedUser.business_category1_id || null,
        business_category2_id: editedUser.business_category2_id || null,
        business_category3_id: editedUser.business_category3_id || null,
        internal_memo: editedUser.internal_memo || ''
      };

/*
      if (editedUser.password && editedUser.password.trim()) {
        updateData.password = editedUser.password;
      }
*/
      await axios.put(`/admin/users/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(editedUser);
      setShowEditModal(false);
      alert('クライアント情報が更新されました。');
    } catch (error: any) {
      alert(error.response?.data?.message || 'クライアント情報の更新に失敗しました。');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditedUser(null);
    setErrors({});
  };


  if (loading) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard-container">
        <AdminHeader />

        <Container>
          <AdminNavigation />
          <Alert variant="danger">クライアントの詳細情報が見つかりません。</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <AdminHeader />

      <Container>
        <AdminNavigation />

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex align-items-center mb-3">
          <h2 className="client-detail-title me-3">{user.name}</h2>
          <Button 
            variant="outline-secondary"
            size="sm"
            onClick={handleEditClient}
            className="edit-icon-button"
            title="クライアント情報を編集"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </div>
        <div className="client-detail-section mb-4">
          <Row>
            <Col className="client-detail-label" md={1}>担当者名</Col>
            <Col className="client-detail-value" md={2}>{user.contact_person || '-'}</Col>
            <Col className="client-detail-label" md={1}>電話番号</Col>
            <Col className="client-detail-value" md={2}>{user.phone || '-'}</Col>
            <Col className="client-detail-label" md={2}>メールアドレス</Col>
            <Col className="client-detail-value" md={4}>{user.email}</Col>
          </Row>
          <Row>
            <Col className="client-detail-label" md={1}>業界・業種</Col>
            <Col className="client-detail-value" md={11}>
              {user.business_category1_id ? (
                <div>
                  {categoryNames.category1 && <>{categoryNames.category1}</>}
                  {categoryNames.category2 && <> &gt; </>}
                  {categoryNames.category2 && <>{categoryNames.category2}</>}
                  {categoryNames.category3 && <> &gt; </>}
                  {categoryNames.category3 && <>{categoryNames.category3}</>}
                </div>
              ) : '未設定'}
            </Col>
          </Row>
        </div>

        {/* 社内向けメモ */}
        <p className="internal-memo-title">社内向けメモ</p>
        <Row className="mb-4">
          <Col md={8}>
            <Form.Control
              as="textarea"
              rows={3}
              value={user.internal_memo}
              placeholder=""
              className="form-textarea internal-memo"
              readOnly
            />
          </Col>
          <Col md={4} className='d-none'>
            <Button className="narisumashi-check">
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              PDFダウンロード
            </Button>
          </Col>
        </Row>

        <div className="candidates-section">
          <div className="search-container d-none">
            <div className="search-input-container">
              <Form.Control
                type="text"
                placeholder="候補者名／メールアドレス／電話番号"
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
          </div>

          <div className="candidates-table-container">
            <table className="candidates-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    候補者名 {getSortIcon('name', sortConfig)}
                  </th>
                  <th onClick={() => handleSort('phone')} className="sortable">
                    電話番号 {getSortIcon('phone', sortConfig)}
                  </th>
                  <th onClick={() => handleSort('email')} className="sortable">
                    メールアドレス {getSortIcon('email', sortConfig)}
                  </th>
                  <th className="text-center">回答数</th>
                  <th onClick={() => handleSort('created_at')} className="sortable">
                    登録日 {getSortIcon('created_at', sortConfig)}
                  </th>
                  <th className="text-center">操作</th>
                </tr>
              </thead>
              <tbody>
                {candidatesLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <div className="blank-form-container">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">候補者情報を読み込んでいます...</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  candidates.data?.length > 0 ? (
                    candidates.data.map((candidate: Candidate) => (
                      <tr key={candidate.id}>
                        <td className="text-left">{candidate.name}</td>
                        <td>{candidate.phone || '-'}</td>
                        <td>{candidate.email}</td>
                        <td className="text-center">{candidate.respondents_count || 0} / {candidate.responses_count || 0}</td>
                        <td>{candidate.created_at ? new Date(candidate.created_at).toLocaleDateString('ja-JP') : '-'}</td>
                        <td className="text-center">
                          <Button 
                            className="detail-button me-1"
                            onClick={() => navigate(`/admin/candidates/${candidate.id}`)}
                          >
                            詳細
                          </Button>
                          <Button 
                            className="delete-button-icon"
                            onClick={() => handleDelete(candidate.id, candidate.name)}
                            title="削除"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">該当するデータはありません。</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {candidates.last_page > 1 && (
            <div className="pagination-container">
              {renderPagination(currentPage, candidates.last_page, paginate)}
            </div>
          )}
        </div>

        {/* Client Edit Modal */}
        <Modal
          show={showEditModal} 
          onHide={handleCancelEdit}
          centered
          size="lg"
          className="client-edit-modal"
          backdrop="static"  // Prevent close when clicking outside
          keyboard={false}   // Optional: Prevent closing with ESC key
        >
          <Modal.Header closeButton>
            <Modal.Title>クライアント詳細</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editedUser && (
              <div className='candidate-edit-panel'>
                <Form>
                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">クライアント名 <span className="required-mark">*</span></div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      type="text"
                      name="name"
                      value={editedUser.name}
                      onChange={handleEditInputChange}
                      isInvalid={!!errors.name}
                      placeholder="クライアント名を入力してください"
                    />
                    {errors.name && (
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">クライアントID</div>
                  <div className="candidate-detail-value" style={{ lineHeight: '40px' }}>
                    {editedUser.id.toString().padStart(5, '0')}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">担当者名 <span className="required-mark">*</span></div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      type="text"
                      name="contact_person"
                      value={editedUser.contact_person || ''}
                      onChange={handleEditInputChange}
                      isInvalid={!!errors.contact_person}
                      placeholder="担当者名を入力してください"
                    />
                    {errors.contact_person && (
                      <Form.Control.Feedback type="invalid">
                        {errors.contact_person}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">電話番号 <span className="required-mark">*</span></div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      type="text"
                      name="phone"
                      value={editedUser.phone || ''}
                      onChange={handleEditInputChange}
                      isInvalid={!!errors.phone}
                      placeholder="電話番号を入力してください"
                    />
                    {errors.phone && (
                      <Form.Control.Feedback type="invalid">
                        {errors.phone}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">メールアドレス <span className="required-mark">*</span></div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      type="email"
                      name="email"
                      value={editedUser.email}
                      onChange={handleEditInputChange}
                      isInvalid={!!errors.email}
                      placeholder="メールアドレスを入力してください"
                    />
                    {errors.email && (
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">業界・業種</div>
                  <div className="candidate-detail-value">
                    <BusinessCategorySelector
                      allCategory1Data={allCategory1Data}
                      allCategory2Data={allCategory2Data}
                      allCategory3Data={allCategory3Data}
                      category1Value={editedUser.business_category1_id || 0}
                      category2Value={editedUser.business_category2_id || 0}
                      category3Value={editedUser.business_category3_id || 0}
                      onCategory1Change={(value) => setEditedUser(prev => prev ? { 
                        ...prev, 
                        business_category1_id: value,
                        business_category2_id: 0,
                        business_category3_id: 0
                      } : null)}
                      onCategory2Change={(value) => setEditedUser(prev => prev ? { 
                        ...prev, 
                        business_category2_id: value,
                        business_category3_id: 0
                      } : null)}
                      onCategory3Change={(value) => setEditedUser(prev => prev ? { ...prev, business_category3_id: value } : null)}
                      errors={errors}
                    />
                  </div>
                </div>

                <div className="candidate-detail-item d-none">
                  <div className="candidate-detail-label">パスワード</div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      type="password"
                      name="password"
                      value={editedUser.password || ''}
                      onChange={handleEditInputChange}
                      isInvalid={!!errors.password}
                      placeholder="パスワードを変更する場合のみ入力してください"
                    />
                    {errors.password && (
                      <Form.Control.Feedback type="invalid">
                        {errors.password}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>

                <div className="candidate-detail-item">
                  <div className="candidate-detail-label">社内向けメモ</div>
                  <div className="candidate-detail-value">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="internal_memo"
                      value={editedUser.internal_memo || ''}
                      onChange={handleEditInputChange}
                      placeholder="社内向けメモを入力してください"
                    />
                    {errors.internal_memo && (
                      <Form.Control.Feedback type="invalid">
                        {errors.internal_memo}
                      </Form.Control.Feedback>
                    )}
                  </div>
                </div>
                </Form>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="success" 
              className="save-button"
              onClick={handleSaveClient}
              disabled={isEditSubmitting}
            >
              {isEditSubmitting ? '登録中...' : '登録'}
            </Button>
            <Button 
              variant="secondary" 
              className="cancel-button"
              onClick={handleCancelEdit}
              disabled={isEditSubmitting}
            >
              キャンセル
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </div>
  );
};

export default AdminClientDetail;
